import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { FilterProps } from '../../common/Filter/types'
import {
    SelectFilter,
    SelectFilterProps,
} from '../../common/Filter/SelectFilter/SelectFilter'
import { iconMap } from '@/utils/constants'
import { Icon } from '@/components/atoms'
import { GetCommoditiesRequestParams } from '@/hooks/useApi/useFiltersApi/types'

export type CommodityFilterProps = {
    name: string
    onChange: (commodityType: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    multiple?: boolean
    className?: string
    params: Partial<GetCommoditiesRequestParams>
} & Pick<FilterProps, 'containerRef'>

export const CommodityFilter: React.FC<CommodityFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, multiple, params, onChange } =
        props

    const { useCommoditiesQuery } = useFiltersApi()

    const query = useCommoditiesQuery(params)

    const { data, isLoading, isFetched, isFetching, isError } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const options = data?.map((commodity) => {
        return {
            value: commodity.commodityTypeId.toString(),
            label: commodity.commodityName,
        } as Option
    })

    const passedProps: SelectFilterProps = {
        multiple,
        name,
        disabled: !!disabled,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        value: value,
        defaultValue: defaultValue,
        label: t(`filters.commodity.label`),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
        required: true,
        renderLabel: (option: Option) => {
            const commodity = data?.find(
                (c) => c.commodityTypeId.toString() === option.value
            )
            if (!commodity) return <>{option.label}</>

            return (
                <div className="flex items-center gap-1">
                    <Icon
                        name={iconMap[commodity.commodityTypeId] || 'info'}
                        size="small"
                        data-testid={`${commodity.commodityName}-icon`}
                    />
                    {commodity.commodityName}
                </div>
            )
        },
    }

    return <SelectFilter {...passedProps} />
}
